import { cloneDeep, get, toInteger } from 'lodash';
import tinycolor from 'tinycolor2';
import {
    bgVirtualBridge,
    complexBridge,
    createCanvas as createCanvasWasm,
    filterBridge,
    fixZipResUrl,
    getStripArea,
    MTIKProjectionType,
} from '@meitu/wasm-lib';
import assetsConfig from '@meitu/wasm-lib/assetsConfig.json';
import {
    MTIKComplexGlowConfig,
    MTIKComplexProjectionConfig,
    MTIKComplexShadowConfig,
    MTIKComplexStrokeConfig,
} from '@meitu/wasm-lib/typings/lib/define/complexDefine';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, isMd5Uri, loadImageAsync } from '@/utils/file-kit';
import { uploadBase64ToObs } from '@/utils/upload-kit/obs-manager';
import { IImageEffectShadowType, IImageLayerProps, ILayerProps, LayerType } from '@/version-types';
import { ComplexBridgeFilterID, getImageEffectResultImgSize } from '../constants/image-layer-constant';

const GlassBlurFilterID = 'Uglass';

const ClassicBlurFilterID = 'classBlur';

export const AR_RES_ID = 'ar_res';

let isLoadedAssets = false;

/**
 * 获取图片图层图片然后再外扩三倍
 *
 * @param layer - 图层属性
 * @param layerResultImage - 图层结果图片
 * @returns HTMLCanvasElement
 */
export const getImageLayerScaleCanvas = async (
    layer: ILayerProps,
    layerResultImage: HTMLCanvasElement,
    imgEffectResultImgSize: number,
) => {
    if (!layerResultImage) {
        return;
    }
    const originWidth = layer.width;
    const originHeight = layer.height;

    const scaleCanvas = document.createElement('canvas');
    scaleCanvas.width = originWidth * imgEffectResultImgSize;
    scaleCanvas.height = originHeight * imgEffectResultImgSize;

    const ctx = scaleCanvas.getContext('2d');
    ctx?.drawImage(
        layerResultImage,
        0,
        0,
        originWidth,
        originHeight,
        (scaleCanvas.width - originWidth) / 2,
        (scaleCanvas.height - originHeight) / 2,
        originWidth,
        originHeight,
    );

    return scaleCanvas;
};

export const getEffectParams = (layer: ILayerProps) => {
    if (!layer) return '';
    const { width, height } = layer;
    const data = cloneDeep(layer.data) as any;
    delete data.screenshotUrl;
    return JSON.stringify({
        width,
        height,
        ...data,
    });
};

export const EmptyEffect = [
    {
        mComplexType: 2,
        mColorR: 1,
        mColorG: 1,
        mColorB: 1,
        mColorA: 1,
        mEditColorEnable: false,
        mEditSizeEnable: false,
        mEnable: false,
        mLayerStyle: 0,
        mSize: 0.18,
    },
    {
        mComplexType: 1,
        mBlur: 0,
        mColorR: 0,
        mColorG: 0,
        mColorB: 0,
        mColorA: 0,
        mEditColorEnable: false,
        mEditSizeEnable: false,
        mEnable: false,
        mLayerStyle: 6,
    },
    {
        mComplexType: 0,
        mBlur: 0.6,
        mColorR: 0,
        mColorG: 0,
        mColorB: 0,
        mColorA: 0.4980392156862745,
        mEditColorEnable: false,
        mEditSizeEnable: false,
        mEnable: false,
        mLayerStyle: 7,
        mOffsetX: 0,
        mOffsetY: 0,
    },
];

class ImageEffectAction {
    constructor() {}
    static isEmptyEffect(layerData: IImageLayerProps) {
        return !(
            layerData.effect &&
            (layerData.effect.stroke?.length > 0 ||
                layerData.effect.glow?.length > 0 ||
                layerData.effect.shadow?.length > 0)
        );
    }

    static closeGLRender(layer: ILayerProps) {
        layer.__renderEnhanceCanvas = false;
        layer.__renderGLCanvas = false;
    }

    static async glRender(layer: ILayerProps, layerResultImage: HTMLCanvasElement) {
        if (!layer) return false;

        if (layer.layerType !== LayerType.IMAGE_LAYER) {
            return false;
        }

        const imgEffectResultImgSize = getImageEffectResultImgSize(layer.data as IImageLayerProps);
        complexBridge.setIsEnableBlend(false);
        complexBridge.setExpandCanvasFactor(imgEffectResultImgSize);
        complexBridge.setCanvasExpandRatio(imgEffectResultImgSize);
        complexBridge.setIsDrawSdf(true);
        if ((layer.data as IImageLayerProps).borderRadius && !(layer.data as IImageLayerProps).borderRadius?.hidden) {
            const { borderRadius } = layer.data as IImageLayerProps;
            if (
                !borderRadius?.hidden &&
                (borderRadius?.leftBottom ||
                    borderRadius?.leftTop ||
                    borderRadius?.rightBottom ||
                    borderRadius?.rightTop)
            ) {
                complexBridge.setIsNeedUpdateSdf(true);
                complexBridge.setIsDrawRound(true);
                const size = Math.min(layer.width, layer.height) / 2;
                complexBridge.setRoundRadius(
                    borderRadius.leftTop * size * 0.01,
                    borderRadius.leftBottom * size * 0.01,
                    borderRadius.rightTop * size * 0.01,
                    borderRadius.rightBottom * size * 0.01,
                );
            }
        }

        const scaleCanvas = (await getImageLayerScaleCanvas(
            layer,
            layerResultImage,
            imgEffectResultImgSize,
        )) as HTMLCanvasElement;
        const { width, height } = layer;
        const { canvas: originCanvas } = createCanvas(
            width * imgEffectResultImgSize,
            height * imgEffectResultImgSize,
            scaleCanvas,
        );

        await complexBridge
            .from(originCanvas)
            .apply(
                ComplexBridgeFilterID,
                fixZipResUrl(assetsConfig.complex_multiConfig),
                1,
                'configuration.plist',
                fixZipResUrl(assetsConfig.complex_SmearRes),
                false,
                false,
            );

        const stripArea = getStripArea(layerResultImage);
        const ratio = (layerResultImage.height - stripArea.y - stripArea.height) / scaleCanvas.height;
        complexBridge.setBottomOffset(ratio);
        await ImageEffectAction.processRender(layer);

        return false;
    }

    static async getEffectPosition(layer: ILayerProps) {
        const { width, height } = layer;
        const [left, right, top, bottom] = [0, width, 0, height];
        const position = complexBridge.sticker2ExpandPosition(left, top, right, bottom);
        return position;
    }

    static async processRender(layer: ILayerProps, isUpdateBorderRadius = true) {
        const imgEffectResultImgSize = getImageEffectResultImgSize(layer.data as IImageLayerProps);
        complexBridge.setIsEnableBlend(false);
        complexBridge.setExpandCanvasFactor(imgEffectResultImgSize);
        complexBridge.setCanvasExpandRatio(imgEffectResultImgSize);
        const lowLevelData = ImageEffectAction.transferLayerToLowLevelData(layer);

        console.log('lowLevelData===', lowLevelData);
        if (isUpdateBorderRadius) {
            complexBridge.setIsDrawRound(true);
            complexBridge.setIsDrawSdf(true);
            const { borderRadius } = layer.data as IImageLayerProps;
            if (!borderRadius || borderRadius.hidden) {
                complexBridge.setRoundRadius(0, 0, 0, 0);
            } else if (borderRadius && !borderRadius.hidden) {
                const size = Math.min(layer.width, layer.height) / 2;
                complexBridge.setRoundRadius(
                    borderRadius.leftTop * size * 0.01,
                    borderRadius.leftBottom * size * 0.01,
                    borderRadius.rightTop * size * 0.01,
                    borderRadius.rightBottom * size * 0.01,
                );
            }
        }
        complexBridge.setEnableTotalControl(false);
        complexBridge.setComplexFilterConfigArray(lowLevelData as any);

        await complexBridge.processRender();

        const position = await ImageEffectAction.getEffectPosition(layer);

        if ((layer.data as IImageLayerProps)?.effect)
            (layer.data as IImageLayerProps).effect!.effectPosition = position;

        await ImageEffectAction.displayView(layer);

        complexBridge.setIsNeedUpdateSdf(true);
    }

    static async displayView(layer: ILayerProps) {
        const { canvas } = await complexBridge.getGLResult();
        const container = document.getElementById(`${layer.id}GLContainer`);
        const position = (layer.data as IImageLayerProps)?.effect?.effectPosition;
        if (!canvas || !container) return;
        if (container.children.length > 0) container.removeChild(container?.children[0]);
        container.appendChild(canvas!);
        if (!position) return;
        canvas.style.width = container.style.width = position.right - position.left + 'px';
        canvas.style.height = container.style.height = position.bottom - position.top + 'px';
        container.style.transform = `translate(${position.left}px, ${position.top}px)`;
    }

    static async getScreenshotUrl(layer: ILayerProps) {
        const params = getEffectParams(layer);
        if (!params) return null;
        const code = base64Kit.md5(params);
        const localUrl = base64Kit.getLocalUrl(code);
        if (!isMd5Uri(localUrl)) {
            return code;
        } else {
            const result = await complexBridge.getGLResult();
            const resUrl = result.canvas?.toDataURL();
            return base64Kit.getCode(resUrl!, params);
        }
    }

    static async runEffectAndGetUrl(layer: ILayerProps, layerResultImage: HTMLCanvasElement) {
        await ImageEffectAction.glRender(layer, layerResultImage);
        const url = await ImageEffectAction.getScreenshotUrl(layer);
        return url;
    }

    static transferLayerToLowLevelData(layer: ILayerProps) {
        const { data } = layer;
        const { effect } = data as IImageLayerProps;

        if (!effect) {
            return EmptyEffect;
        }

        const lowLevelData = [] as (MTIKComplexStrokeConfig | MTIKComplexGlowConfig | MTIKComplexShadowConfig)[];
        const { glow, shadow, stroke } = effect;

        // 处理描边效果，mComplexType = 2
        if (stroke.length !== 0 && !stroke[0].hidden) {
            const { width, color, strokeType } = stroke[0];
            const { r, g, b, a } = tinycolor(color).toRgb();
            const strokeData = {} as MTIKComplexStrokeConfig;

            strokeData.mComplexType = 2;
            strokeData.mColorR = r / 255;
            strokeData.mColorG = g / 255;
            strokeData.mColorB = b / 255;
            strokeData.mColorA = a;
            strokeData.mEditColorEnable = true;
            strokeData.mEditSizeEnable = true;
            strokeData.mEnable = width !== 0;
            strokeData.mLayerStyle = strokeType;
            strokeData.mIsRender = true;
            strokeData.mSize = width;
            lowLevelData.push(strokeData);
        } else {
            const strokeData = {} as MTIKComplexStrokeConfig;
            strokeData.mComplexType = 2;
            strokeData.mColorR = 0;
            strokeData.mColorG = 0;
            strokeData.mColorB = 0;
            strokeData.mColorA = 0;
            strokeData.mEditColorEnable = false;
            strokeData.mEditSizeEnable = false;
            strokeData.mEnable = false;
            strokeData.mIsRender = true;
            strokeData.mLayerStyle = 0;
            strokeData.mSize = 0;
            lowLevelData.push(strokeData);
        }
        // 处理发光效果，mComplexType = 1
        if (glow.length !== 0 && !glow[0].hidden) {
            const { width, color } = glow[0];
            const { r, g, b, a } = tinycolor(color).toRgb();
            const glowData = {} as MTIKComplexGlowConfig;

            glowData.mComplexType = 1;
            glowData.mBlur = width;
            glowData.mColorR = r / 255;
            glowData.mColorG = g / 255;
            glowData.mColorB = b / 255;
            glowData.mColorA = a;
            glowData.mEditColorEnable = true;
            glowData.mEditSizeEnable = width !== 0;
            glowData.mEnable = true;
            glowData.mLayerStyle = 0;
            glowData.mIsRender = true;
            lowLevelData.push(glowData);
        } else {
            const glowData = {} as MTIKComplexGlowConfig;
            glowData.mComplexType = 1;
            glowData.mBlur = 0;
            glowData.mColorR = 0;
            glowData.mColorG = 0;
            glowData.mColorB = 0;
            glowData.mColorA = 0;
            glowData.mEditColorEnable = false;
            glowData.mEditSizeEnable = false;
            glowData.mEnable = false;
            glowData.mIsRender = true;
            glowData.mLayerStyle = 0;
            lowLevelData.push(glowData);
        }

        // 处理阴影效果，mComplexType = 0
        if (shadow.length !== 0 && !shadow[0].hidden) {
            const { x, y, blur, color, type, rotate, direction, distance, wave, waveNum, brightness, opacity } =
                shadow[0];
            const { r, g, b, a } = tinycolor(color).toRgb();
            let shadowData;

            if (type === IImageEffectShadowType.base) {
                shadowData = {} as MTIKComplexShadowConfig;
                shadowData.mComplexType = 0;
                shadowData.mBlur = blur;
                shadowData.mColorR = r / 255;
                shadowData.mColorG = g / 255;
                shadowData.mColorB = b / 255;
                shadowData.mColorA = a;
                shadowData.mEditColorEnable = true;
                shadowData.mEditSizeEnable = true;
                shadowData.mEnable = true;
                shadowData.mLayerStyle = 0;
                shadowData.mIsRender = true;
                shadowData.mOffsetX = x;
                shadowData.mOffsetY = y;
                shadowData.mRotation = rotate || 0;
            } else if (type === IImageEffectShadowType.lightProjection) {
                shadowData = {} as MTIKComplexProjectionConfig;
                shadowData.mComplexType = 4;
                shadowData.mEditColorEnable = true;
                shadowData.mEditSizeEnable = true;
                shadowData.mEnable = true;
                shadowData.mLayerStyle = 0;
                shadowData.mIsRender = true;
                shadowData.mColorR = r / 255;
                shadowData.mColorG = g / 255;
                shadowData.mColorB = b / 255;
                shadowData.mColorA = a;
                shadowData.mBlur = blur;
                shadowData.mDirection = direction || 0;
                shadowData.mDistance = (distance || 0) / 100;
                shadowData.mOffsetY = -(y || 0) / 100;
            } else if (type === IImageEffectShadowType.reflection) {
                shadowData = {} as MTIKComplexProjectionConfig;
                shadowData.mComplexType = 4;
                shadowData.mEditColorEnable = true;
                shadowData.mEditSizeEnable = true;
                shadowData.mEnable = true;
                shadowData.mLayerStyle = MTIKProjectionType.MTIKProjectionTypeInverted;
                shadowData.mIsRender = true;
                shadowData.mBlur = blur;
                shadowData.mDistance = (distance || 0) / 100;
                shadowData.mBrightness = (brightness || 0) / 100;
                shadowData.mColorA = opacity || 1;
                shadowData.mOffsetX = (x || 0) / 100;
                shadowData.mOffsetY = -(y || 0) / 100;
            } else if (type === IImageEffectShadowType.waterSurface) {
                shadowData = {} as MTIKComplexProjectionConfig;
                shadowData.mComplexType = 4;
                shadowData.mEditColorEnable = true;
                shadowData.mEditSizeEnable = true;
                shadowData.mEnable = true;
                shadowData.mLayerStyle = MTIKProjectionType.MTIKProjectionTypeWater;
                shadowData.mIsRender = true;
                shadowData.mBlur = blur;
                shadowData.mWave = (wave || 0) / 100;
                shadowData.mNum = waveNum === undefined ? 10 : waveNum;
                shadowData.mColorA = opacity || 1;
                shadowData.mDistance = 1;
                shadowData.mOffsetY = -(y || 0) / 100;
            }
            lowLevelData.push(shadowData as any);

            if (type === IImageEffectShadowType.base) {
                lowLevelData.push({
                    mComplexType: 4,
                    mEnable: false,
                } as any);
            } else {
                lowLevelData.push({
                    mComplexType: 0,
                    mEnable: false,
                } as any);
            }
        } else {
            const shadowData = {} as MTIKComplexShadowConfig;
            shadowData.mComplexType = 0;
            shadowData.mBlur = 0;
            shadowData.mColorR = 0;
            shadowData.mColorG = 0;
            shadowData.mColorB = 0;
            shadowData.mColorA = 0;
            shadowData.mEditColorEnable = false;
            shadowData.mEditSizeEnable = false;
            shadowData.mEnable = false;
            shadowData.mIsRender = true;
            shadowData.mLayerStyle = 0;
            shadowData.mOffsetX = 0;
            shadowData.mOffsetY = 0;
            lowLevelData.push(shadowData);
            lowLevelData.push({
                mComplexType: 4,
                mEnable: false,
            } as any);
        }

        return lowLevelData;
    }

    static async loadClassicGlassAssets() {
        if (isLoadedAssets) return;
        await Promise.all([
            // 加载基础文字资源
            bgVirtualBridge.loadEffectZip(
                ClassicBlurFilterID,
                fixZipResUrl(assetsConfig.bgVirtual_newEffect_classBlur),
            ),
        ]);
        isLoadedAssets = true;
    }

    static async renderImageGlassBlur(layer: ILayerProps) {
        const { opacity, width, height, id } = layer;
        const {
            imageTransform: { widthRatio, heightRatio },
        } = layer.data as IImageLayerProps;

        const originImgWidth = toInteger(width * widthRatio);
        const originImgHeight = toInteger(height * heightRatio);
        const img = await loadImageAsync((layer.data as IImageLayerProps).url);
        const canvas = document.createElement('canvas');
        canvas.width = originImgWidth;
        canvas.height = originImgHeight;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, img.width, img.height, 0, 0, originImgWidth, originImgHeight);

        if (!canvas) {
            return;
        }
        const fromCanvas = createCanvasWasm(originImgWidth, originImgHeight, canvas);
        const defaultAlpha = await filterBridge.getDefaultAlpha(
            GlassBlurFilterID,
            fixZipResUrl(assetsConfig.filter_Uglass),
        );

        // 一些默认资源路径
        const AR_RES_ID = 'ar_res';
        const arResPath = assetsConfig.text_ar_res;
        const filter_path = 'filter/configuration_filter.plist';
        const ar_path = 'ar/configuration.plist';
        // 是否使用普通滤镜效果
        const isUseNormalEffect = false;
        // 是否使用 ar 效果
        const isUseArEffect = false; // true
        // 是否使用 mcp 效果
        const isUseMcpEffect = true; // false

        await filterBridge.from(fromCanvas).applyWithNoResult(
            GlassBlurFilterID, // 素材 id
            fixZipResUrl(assetsConfig.filter_Uglass), // 素材压缩包路径
            1, // 默认程度值
            filter_path, // 普通滤镜素材包路径(可忽略)
            ar_path, // ar滤镜素材包路径(可忽略)
            AR_RES_ID, // ar 素材包文件夹
            arResPath, // ar 资源包路径
            isUseArEffect, // 是否 ar 效果
            isUseMcpEffect, // 是否 mcp 效果
            true, // 是否需要初始化 view(首次加载需要)
        );
    }

    /**
     * 改变玻璃模糊模糊度
     *
     * @param alpha - 模糊度
     */
    static async changeImageGlassBlurAlpha(alpha: number) {
        // 7: 玻璃数量: 0-1 映射值 1-80
        // 8: 模糊程度: 0-1 映射值 0-1
        // 9: 偏移程度: 0-1 映射值 0-1
        await filterBridge.updateMcpSliderParam(alpha / 100, 7, true);
    }

    /**
     * 获取玻璃模糊结果图片链接地址
     */
    static async getImageGlassBlurResultUrl() {
        const { canvas: resultCanvas } = await filterBridge.getGLResult();
        const resUrl = resultCanvas?.toDataURL();

        const { url } = await uploadBase64ToObs(resUrl!);
        return url;
    }

    static async renderClassicBlur(layer: ILayerProps, classicBlurParams: any) {
        await ImageEffectAction.loadClassicGlassAssets();
        const { opacity, width, height, id } = layer;
        const {
            imageTransform: { widthRatio, heightRatio },
        } = layer.data as IImageLayerProps;

        const originImgWidth = toInteger(width * widthRatio);
        const originImgHeight = toInteger(height * heightRatio);
        const img = await loadImageAsync((layer.data as IImageLayerProps).url);
        const canvas = document.createElement('canvas');
        canvas.width = originImgWidth;
        canvas.height = originImgHeight;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, img.width, img.height, 0, 0, originImgWidth, originImgHeight);

        if (!canvas) {
            return;
        }

        const fromCanvas = createCanvasWasm(originImgWidth, originImgHeight, canvas);
        await bgVirtualBridge
            .from(fromCanvas)
            .apply(
                ClassicBlurFilterID,
                fixZipResUrl(assetsConfig.bgVirtual_newEffect_classBlur),
                classicBlurParams,
                `${ClassicBlurFilterID}`,
                AR_RES_ID,
                fixZipResUrl(assetsConfig.text_ar_res),
                '',
                '',
                '',
                true,
                true,
            );
    }

    /**
     * 改变经典模糊模糊度
     *
     * @param alpha - 模糊度
     * @param classicBlurParams - 经典模糊参数
     */
    static async changeClassicBlurAlpha(alpha: number, classicBlurParams: any) {
        classicBlurParams.mFocalScale = alpha / 100;
        classicBlurParams.mUpdateEffect = classicBlurParams.mUpdateEffect + 1;
        bgVirtualBridge.setParams(classicBlurParams);
        await bgVirtualBridge.processRender();
    }

    /**
     * 获取经典模糊结果图片链接地址
     */
    static async getImageClassicBlurResultUrl() {
        const { canvas: resultCanvas } = await bgVirtualBridge.getGLResult();
        const resUrl = resultCanvas?.toDataURL();
        const { url } = await uploadBase64ToObs(resUrl!);
        return url;
    }
}

export default ImageEffectAction;
